import { FocusEvent, forwardRef, isValidElement, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactSelect, { MultiValue, OptionsOrGroups, SelectInstance, SingleValue } from "react-select";

import { LoadingGroup } from "capitalrx-components-2.0/lib/general";

import { AccessibilityProps } from "../../AccessibilityProps";
import { isDescendentOf } from "../../utilities/isDescendentOf";
import { DropdownProps } from "../Dropdown";
import { GroupItem } from "../Dropdown/GroupItem";
import { OptionItem } from "../Dropdown/OptionItem";
import * as Components from "../Dropdown/components";
import { flattenItems, getItems } from "../Dropdown/helpers";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { useSubmitting } from "../SubmitGroup";
import { Placeholder } from "../Textbox/Placeholder";
import { useSimpleInputState } from "../useSimpleInputState";
import { normalizeAutocompleteValue } from "../utils/autocompleteHelpers";
import { Option } from "./components/Option";

/**
 * The `CustomSelect` component properties.
 */
export interface CustomSelectProps extends Omit<DropdownProps, "tooltip">, Pick<AccessibilityProps, "aria-label"> {
  /**
   * The input ID.
   */
  readonly id: string;

  /**
   * Indicates that the behavior of search is managed manually.
   */
  readonly manualSearchFilter?: boolean;

  /**
   * The text input value.
   */
  readonly inputValue: string;

  /**
   * Called when text is typed in the input.
   */
  readonly setInputValue: React.Dispatch<React.SetStateAction<string>>;
}

/**
 * The dropdown component.
 */
export const CustomSelect = forwardRef<SelectInstance<OptionItem | null, boolean>, CustomSelectProps>(function ShareDropdown(props, ref) {
  const {
    id,
    children,
    prefix,
    suffix,
    placeholder = "",
    tabIndex,
    autoFocus,
    defaultValue = null,
    clearable = false,
    searchInputProps,
    manualSearchFilter = false,
    noOptionsMessage,
    inputValue,
    label,
    hideLabel,
    "aria-label": ariaLabel,
    setInputValue,
    onChange,
    onFocusEnter,
    onFocusExit,
    onSearchChange: onSearchChangeProp,
    onMenuScrollToBottom,
    autoComplete = "off",
  } = props;
  const { value = defaultValue } = props;

  const getLabelText = (label: React.ReactNode): string | undefined => {
    if (typeof label === "string") return label;
    if (isValidElement(label) && typeof label.props.children === "string") {
      return label.props.children;
    }
    if (Array.isArray(label)) {
      return label.map(getLabelText).filter(Boolean).join(" ");
    }
    return undefined;
  };

  const items: OptionsOrGroups<OptionItem, GroupItem> = getItems(children);
  const flattenedItems = flattenItems(items);
  const [menuState, setMenuState] = useState({
    cancelled: false,
    open: false,
  });
  const parentRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [searchValue, onSearchChange] = useSimpleInputState<string | null>("");

  const changeHandler = useCallback(
    (newValue: SingleValue<OptionItem | null> | MultiValue<OptionItem | null>) => {
      if (Array.isArray(newValue)) return;
      const singleValue = newValue as SingleValue<OptionItem>;
      onChange?.(singleValue?.value ?? null, value);
    },
    [onChange, value],
  );

  const focusHandler = useCallback(() => {
    onFocusEnter?.();
  }, [onFocusEnter]);

  const blurHandler = useCallback(
    (ev: FocusEvent) => {
      const { relatedTarget } = ev;

      if (parentRef.current && relatedTarget) {
        if (isDescendentOf(parentRef.current, relatedTarget)) {
          setMenuState((current) => {
            return {
              ...current,
              cancelled: true,
            };
          });
          return;
        }
      }

      onFocusExit?.();
    },
    [onFocusExit],
  );

  const menuOpenHandler = useCallback(() => {
    setMenuState((current) => {
      if (current.cancelled) {
        return {
          ...current,
          cancelled: false,
        };
      }

      return {
        cancelled: false,
        open: true,
      };
    });
  }, []);

  const menuCloseHandler = useCallback(() => {
    setMenuState((current) => {
      if (current.cancelled) {
        return {
          ...current,
          cancelled: false,
        };
      }

      return {
        cancelled: false,
        open: false,
      };
    });
  }, []);

  useEffect(() => {
    if (menuState.open) {
      onSearchChange("", "");
      onSearchChangeProp?.("", "");
    }
  }, [!menuState.cancelled, menuState.open]);

  const searchBlurHandler = useCallback(
    (ev: FocusEvent) => {
      const { relatedTarget } = ev;

      ev.stopPropagation();

      if (relatedTarget === null || (parentRef.current && !isDescendentOf(parentRef.current, relatedTarget))) {
        setMenuState({
          cancelled: false,
          open: false,
        });

        onFocusExit?.();
      }
    },
    [onFocusExit],
  );

  const filter = useCallback(
    // eslint-disable-next-line
    (item: any) => {
      if (!searchValue) return true;

      const option = item.data as OptionItem | GroupItem;
      if (option.type === "group") return true;

      if (!option.searchText) {
        console.error("Dropdown with search enabled requires options to either have string content or the label property to be set.");
      } else if (option.searchText.toLowerCase().indexOf(searchValue.toLowerCase()) === -1) return false;

      return true;
    },
    [searchValue],
  );

  const selectedItem = useMemo(() => {
    if (value === null) return null;
    if (!flattenedItems || items.length === 0) return null;
    return flattenedItems.find((item) => item.value === value) ?? null;
  }, [flattenedItems, value]);

  const searchChangeHandler = useCallback(
    (value: string | null) => {
      onSearchChange(value, searchValue ?? null);
      onSearchChangeProp?.(value, searchValue ?? null);
    },
    [searchValue, onSearchChange, onSearchChangeProp],
  );

  const scrollToBottomHandler = useCallback(() => {
    onMenuScrollToBottom?.();
  }, [onMenuScrollToBottom]);

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);
  const submitting = useSubmitting();

  const autocompleteValue = useMemo(() => normalizeAutocompleteValue(autoComplete), [autoComplete]);

  useLayoutEffect(() => {
    if (autoComplete === "on") return;
    const comboboxInput = parentRef.current?.querySelector<HTMLInputElement>("input[role='combobox']");
    if (!comboboxInput) return;

    /* react-select renders a hidden dummy input for non-searchable selects.
       That input does not forward an autocomplete attribute, so Chrome can still
       offer autofill suggestions unless we set it ourselves. */
    comboboxInput.setAttribute("autocomplete", autocompleteValue ?? "off");
    comboboxInput.setAttribute("autocapitalize", "none");
    comboboxInput.setAttribute("autocorrect", "off");
    comboboxInput.setAttribute("spellcheck", "false");
  }, [autocompleteValue, autoComplete]);

  // Merge autocomplete into searchInputProps for react-select
  const finalSearchInputProps = useMemo(() => {
    const baseProps = searchInputProps || {};
    return {
      ...baseProps,
      autoComplete: autocompleteValue,
    };
  }, [searchInputProps, autocompleteValue]);

  const accessibleLabel = !label || hideLabel ? `dropdown ${id}` : undefined;

  let finalMenuOpen: boolean | undefined = undefined;

  if (readOnly || submitting) finalMenuOpen = false;

  return (
    <LoadingGroup.Placeholder fallback={<Placeholder />}>
      <div ref={parentRef}>
        <ReactSelect
          ref={ref}
          aria-live="polite"
          aria-label={ariaLabel ? ariaLabel || getLabelText(label) : accessibleLabel}
          aria-labelledby={!label || hideLabel ? undefined : `${id}-label`}
          inputId={id}
          autoFocus={autoFocus}
          tabIndex={readOnly || submitting || disabled ? -1 : (tabIndex ?? 0)}
          isSearchable={true}
          inputValue={inputValue}
          onInputChange={setInputValue}
          isMulti={false}
          isDisabled={disabled}
          isClearable={clearable}
          placeholder={placeholder}
          components={{
            ...Components,
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null,
            Option,
          }}
          options={items}
          value={selectedItem}
          closeMenuOnScroll
          captureMenuScroll
          escapeClearsValue={clearable}
          onChange={changeHandler}
          menuIsOpen={finalMenuOpen}
          openMenuOnClick={!readOnly && !submitting}
          onFocus={focusHandler}
          onBlur={blurHandler}
          onMenuOpen={menuOpenHandler}
          menuShouldScrollIntoView={false}
          onMenuClose={menuCloseHandler}
          onMenuScrollToBottom={scrollToBottomHandler}
          filterOption={!manualSearchFilter ? filter : undefined}
          noOptionsMessage={() => noOptionsMessage || "No options"}
          // custom properties
          {...{
            prefix,
            suffix,
            searchInputProps: finalSearchInputProps,
            searchRef,
            searchValue,
            onSearchChange: searchChangeHandler,
            onSearchBlur: searchBlurHandler,
            isReadOnly: readOnly || submitting,
          }}
        />
      </div>
    </LoadingGroup.Placeholder>
  );
});
